// What a line puts on the invoice, and the two groups a customer's month
// splits into.
//
// THE REPORT STATES OUR SHARE, AND OUR SHARE IS NOT ALWAYS WHAT WE INVOICE. On
// something we sell, the partner collects the gross and the document we raise
// is for our share of it. On something we spent, our share is the part we
// carry, and the document asks the partner for the rest. The screen used to
// print "our share" for both and add them up, which gave a total that was on no
// invoice.
//
// This is the rule the interpretation applies when it prices a clause (the
// `charged` figure in CrmDealInterpretation.kt), restated here because the
// month-close report does not carry that figure. It only ever runs on lines
// still to bill: an issued line's figure is its frozen snapshot, and issued
// lines are not on this screen.
//
// A LINE ALREADY ON AN ISSUED INVOICE IS NOT STILL TO BILL. The report keeps
// those lines, because to the report a month with some lines billed is not a
// closed month. This screen lists what is left to send, so it drops them, and
// drops them from every figure.

import type { MonthCloseLine } from "@crm/shared";
import { assertNever } from "../../lib/assertNever.js";

export type ChargeGroupKind = "revenue" | "cost_recovery";

/** Sales first: they are what a month is mostly about. */
const GROUP_ORDER: readonly ChargeGroupKind[] = ["revenue", "cost_recovery"];

export interface ChargeGroup {
  readonly kind: ChargeGroupKind;
  readonly lines: readonly MonthCloseLine[];
  /** The gross over the lines that have one, leaving out money reaching us by statement. */
  readonly amountMinor: number;
  /** What these lines put on the invoice. */
  readonly invoiceMinor: number;
  /** Lines we raise a document for that have no invoice figure yet, so are in neither sum. */
  readonly unbilledCount: number;
  /** The percentage every line bills, `varies` where they disagree, null where none resolved. */
  readonly sharePct: number | "varies" | null;
}

export function groupKindOf(line: MonthCloseLine): ChargeGroupKind {
  switch (line.kind) {
    case "bill":
      return "revenue";
    case "cost_recharge":
      return "cost_recovery";
    default:
      return assertNever(line.kind, "month close line kind");
  }
}

/**
 * What this line puts on the invoice: our share of a sale, the partner's share
 * of a cost. Null while the amount or the share is unknown, and on a line we
 * raise no document for — never a zero standing in for either.
 */
export function invoiceFigureMinor(line: MonthCloseLine): number | null {
  if (line.gross_minor === null || line.our_share_minor === null) return null;
  if (line.collection !== "we_invoice") return null;
  const kind = groupKindOf(line);
  switch (kind) {
    case "revenue":
      return line.our_share_minor;
    case "cost_recovery":
      // The remainder, so the two halves add back to the cost exactly.
      return line.gross_minor - line.our_share_minor;
    default:
      return assertNever(kind, "charge group");
  }
}

/** The percentage of the amount this line invoices: ours on a sale, the partner's on a cost. */
export function billedSharePct(line: MonthCloseLine): number | null {
  const ours = line.applied_split_pct;
  if (ours === null) return null;
  const kind = groupKindOf(line);
  switch (kind) {
    case "revenue":
      return ours;
    case "cost_recovery":
      // Rounded, so a float subtraction never prints a tail nobody wrote.
      return Math.round((100 - ours) * 1e6) / 1e6;
    default:
      return assertNever(kind, "charge group");
  }
}

export function stillToBill(lines: readonly MonthCloseLine[]): MonthCloseLine[] {
  return lines.filter((line) => line.need !== "invoiced");
}

/** A customer's lines as revenue then cost recovery, each summed; an empty group is left out. */
export function chargeGroups(lines: readonly MonthCloseLine[]): ChargeGroup[] {
  return GROUP_ORDER.map((kind) =>
    summarise(
      kind,
      lines.filter((line) => groupKindOf(line) === kind),
    ),
  ).filter((group) => group.lines.length > 0);
}

export function invoiceTotalMinor(groups: readonly ChargeGroup[]): number {
  return groups.reduce((total, group) => total + group.invoiceMinor, 0);
}

function summarise(kind: ChargeGroupKind, lines: readonly MonthCloseLine[]): ChargeGroup {
  let amountMinor = 0;
  let invoiceMinor = 0;
  let unbilledCount = 0;
  const shares = new Set<number>();
  for (const line of lines) {
    const share = billedSharePct(line);
    if (share !== null) shares.add(share);
    // Owed, and on no document of ours: in neither sum and no count of work.
    if (line.need === "not_invoiced") continue;
    if (line.gross_minor !== null) amountMinor += line.gross_minor;
    const figure = invoiceFigureMinor(line);
    if (figure === null) unbilledCount += 1;
    else invoiceMinor += figure;
  }
  return { kind, lines, amountMinor, invoiceMinor, unbilledCount, sharePct: commonShare(shares) };
}

function commonShare(shares: ReadonlySet<number>): number | "varies" | null {
  if (shares.size > 1) return "varies";
  const [only] = shares;
  return only ?? null;
}
